/**
 * Which of the two post-welcome surfaces is on screen.
 *
 * They are not peers: the rail is the guided run, the checklist is what is left
 * over plus the way back into a run that was closed. The rail SUPERSEDES the
 * checklist — never both at once. That is the same rule DemoGuideGate enforces
 * at its render site; expressed here as a predicate instead so it can be
 * asserted in a test rather than eyeballed in JSX.
 *
 * Import-free for the node environment, and pure so the invariant
 * ("showRail && showChecklist" is unreachable) is directly testable.
 */

import type { OnboardingRailState } from "../types";

export type RailVisibilityInput = {
  /** The gate's phase. The rail only exists after the welcome flow is done. */
  phase: string;
  /** False when the user is not the audience (or the state read failed). */
  eligible: boolean;
  /** Null until the server read lands. */
  hasState: boolean;
  rail: OnboardingRailState | undefined;
  checklistDismissedAt: string | undefined;
};

export function railVisibility(input: RailVisibilityInput): {
  showRail: boolean;
  showChecklist: boolean;
} {
  const base = input.phase === "done" && input.eligible && input.hasState;
  if (!base) return { showRail: false, showChecklist: false };

  const rail = input.rail;

  /**
   * Note this is true while `rail` is still UNDEFINED.
   *
   * beginRail is called from an effect in OnboardingGate, and that effect only
   * runs when the gate decides the rail should be on screen. If visibility
   * required rail to already exist, the effect that creates it could never run
   * and the rail would never appear at all.
   */
  const showRail = !rail || (!rail.completedAt && !rail.exitedAt);

  return {
    showRail,
    // The checklist is the only way back into an exited run, so it has to be
    // available precisely when the rail is not.
    showChecklist: !showRail && !input.checklistDismissedAt,
  };
}
